import { Icon, ICONS } from '@bob/library'
import glueFrame from '@websrc/App/Helpers/glueFrame'
import { CSLocales } from '@websrc/Typings/ContentStack'
import React, { FC, PropsWithChildren, useEffect, useRef, useState } from 'react'

import { thumbnailContainer } from './Video23.css'
import * as SC from './Video23.style'

export type Video23Type = {
  type: '23_video'
  photo_id: string
  token: string
  locale: CSLocales
  forcePause?: boolean
}

export const Video23: FC<PropsWithChildren<Video23Type>> = ({
  token,
  photo_id,
  locale,
  forcePause,
  children: placeholderImage,
}) => {
  const [isPlaying, setIsPlaying] = useState(false)
  const [isFullscreen, setIsFullscreen] = useState(false)
  const [wrapperRatio, setWrapperRatio] = useState<number | null>(null)
  const [videoRatio, setVideoRatio] = useState<number | null>(null)
  const [videoWidth, setVideoWidth] = useState()
  const [videoHeight, setVideoHeight] = useState()
  const [videoScale, setVideoScale] = useState(1)

  const videoIframe = useRef<HTMLIFrameElement>(null)
  const videoWrapper = useRef<HTMLDivElement>(null)
  const videoPlayer = useRef<any>(null)

  const url = `//legoeducation.23video.com/v.ihtml/player.html?token=${token}&source=embed&photo%5fid=${photo_id}&autoPlay=0&autoMute=0&hideBigPlay=1&showDescriptions=0`

  const getVideoDimensions = () => {
    if (videoRatio === null) {
      videoPlayer.current.get('video_aspect_ratio', setVideoRatio)
    }

    if (videoHeight === null) {
      videoPlayer.current.get('video_frames_height', setVideoHeight)
    }

    if (videoWidth === null) {
      videoPlayer.current.get('video_frames_width', setVideoWidth)
    }

    if (wrapperRatio === null && videoWrapper.current) {
      setWrapperRatio(videoWrapper.current.offsetWidth / videoWrapper.current.offsetHeight)
    }
  }

  const initGlueFrame = () => {
    if (videoPlayer.current || !videoIframe.current) {
      return
    }

    videoPlayer.current = glueFrame(videoIframe.current, 'Player')

    if (videoPlayer.current !== null) {
      videoPlayer.current.get('ready', () => {
        getVideoDimensions()

        videoPlayer.current.bind('player:video:loadedmetadata', () => {
          // This event fires once when the video is started
          const splitLocale = locale.split('-')

          const video23Locale =
            locale === 'nb-no' ? 'no_NO' : `${splitLocale[0]}_${splitLocale[1].toUpperCase()}`

          videoPlayer.current.set('subtitleLocale', video23Locale)
        })

        videoPlayer.current.bind(
          'player:video:play player:video:pause player:video:end',
          function (e: any) {
            getVideoDimensions()

            let playing = null

            switch (e) {
              case 'player:video:play':
                playing = true
                break
              case 'player:video:pause':
                playing = false
                break
              case 'player:video:end':
                playing = false
                break
            }

            if (playing !== null) {
              setIsPlaying(playing)
            }
          }
        )
      })
    }
  }

  useEffect(() => {
    if (videoRatio !== null && wrapperRatio !== null) {
      setVideoScale(videoRatio / wrapperRatio < 1 ? videoRatio / wrapperRatio : 1)
    }
  }, [videoRatio, wrapperRatio])

  useEffect(() => {
    if (videoPlayer.current) {
      videoPlayer.current.set('playing', !forcePause && isPlaying)
      setIsFullscreen(videoIframe.current === document.fullscreenElement)
    }
  }, [isPlaying, forcePause])

  useEffect(() => {
    initGlueFrame()
  })

  return (
    <>
      <SC.PlayButton
        aria-label={'play'}
        className={isPlaying ? 'playing' : ''}
        onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
          // To make sure that if a video is shown inside a CampaignSpot, it should not follow the clickable container.
          e.stopPropagation()
          e.preventDefault()
          setIsPlaying(!isPlaying)
        }}
      >
        <Icon background={'#000'} color={'#FFF'} icon={ICONS.PLAY} size={80} />
      </SC.PlayButton>
      <div className={thumbnailContainer({ visibility: isPlaying ? 'hidden' : 'visible' })}>
        {placeholderImage}
      </div>

      <SC.VideoWrapper className={isPlaying ? 'playing' : ''} ref={videoWrapper}>
        <SC.VideoRatioContainer
          style={{
            paddingBottom: `${100 / (videoRatio ? videoRatio : 1)}%`,
            transform: isPlaying ? `scale(${videoScale})` : 'scale(1)',
          }}
        >
          <SC.VideoIframe
            allow={'autoplay; fullscreen'}
            allowFullScreen={true}
            height={videoHeight}
            isFullscreen={isFullscreen}
            isPlaying={isPlaying}
            ref={videoIframe}
            src={url}
            title={'video'}
            width={videoWidth}
          />
        </SC.VideoRatioContainer>
      </SC.VideoWrapper>
    </>
  )
}
